import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { X, Loader2, Share2, Users, Check, Plus } from "lucide-react";

/**
 * One-click share to a study group. Reusable across pages.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - item: { id, kind, ... } — the study item being shared
 *  - defaultTitle?: string
 *  - onPublished?: (post) => void
 */
const ShareToGroupModal = ({ open, onClose, item, defaultTitle, onPublished }) => {
  const [groups, setGroups] = useState(null); // null = loading, [] = none
  const [selected, setSelected] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGroups(null);
    setSelected(null);
    setTitle(defaultTitle || defaultKindTitle(item));
    setBody("");
    api.get("/groups")
      .then(({ data }) => {
        setGroups(data || []);
        if (data && data.length === 1) setSelected(data[0].id);
      })
      .catch(() => setGroups([]));
  }, [open, item, defaultTitle]);

  if (!open || !item) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!selected) { toast.error("Pick a group first"); return; }
    if (!title.trim()) { toast.error("Give it a title"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/groups/publish", {
        group_id: selected,
        study_item_id: item.id,
        title: title.trim(),
        body: body.trim(),
      });
      toast.success("Shared to group");
      onPublished?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to share");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
      onClick={() => !busy && onClose?.()}
      data-testid="share-modal"
    >
      <div className="glass-strong w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between sticky top-0 bg-black/70 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 border border-white/[0.08] bg-white/[0.02] flex items-center justify-center rounded-[2px]">
              <Share2 size={14} className="text-amber-400" />
            </div>
            <div>
              <div className="font-display text-base tightest font-semibold leading-none">Share to a group</div>
              <div className="text-[0.72rem] text-neutral-500 mt-0.5">
                {niceKind(item.kind)} · will be pinned to the group feed
              </div>
            </div>
          </div>
          <button onClick={() => !busy && onClose?.()} className="text-neutral-500 hover:text-white transition-colors" data-testid="share-close">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          {/* group picker */}
          <div>
            <div className="label-mono mb-2 flex items-center gap-2"><Users size={11}/> pick a group</div>
            {groups === null && <div className="text-neutral-500 text-[0.86rem] flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> loading…</div>}
            {Array.isArray(groups) && groups.length === 0 && (
              <div className="border border-dashed border-white/[0.10] p-5 rounded-[2px] text-center">
                <div className="text-[0.86rem] text-neutral-400 mb-3">You haven&apos;t joined any groups yet.</div>
                <Link to="/groups" onClick={() => onClose?.()}>
                  <button type="button" className="btn-primary text-[0.78rem] py-2 px-3" data-testid="share-goto-groups">
                    <Plus size={12}/> Create or join a group
                  </button>
                </Link>
              </div>
            )}
            {Array.isArray(groups) && groups.length > 0 && (
              <div className="grid grid-cols-1 gap-1.5 max-h-56 overflow-y-auto pr-1" data-testid="share-group-list">
                {groups.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setSelected(g.id)}
                    data-testid={`share-group-${g.id}`}
                    className={`flex items-center gap-3 px-3 py-2.5 border rounded-[2px] transition-colors text-left ${
                      selected === g.id
                        ? "border-white/25 bg-white/[0.04]"
                        : "border-white/[0.08] hover:border-white/15 hover:bg-white/[0.02]"
                    }`}
                  >
                    <div className="w-8 h-8 border border-white/[0.08] bg-white/[0.03] flex items-center justify-center rounded-[2px] shrink-0 text-[0.72rem] text-neutral-300">
                      {g.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[0.88rem] font-medium truncate">{g.name}</div>
                      <div className="text-[0.7rem] text-neutral-500 truncate">{g.members?.length || 0} members</div>
                    </div>
                    {selected === g.id && <Check size={13} className="text-amber-400 shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* title */}
          <div>
            <div className="label-mono mb-2">title</div>
            <input
              className="input-cyber"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Flashcards for Newton's laws"
              data-testid="share-title"
              required
            />
          </div>

          {/* optional message */}
          <div>
            <div className="label-mono mb-2">note (optional)</div>
            <textarea
              className="input-cyber min-h-[80px] resize-y"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Add context for your group…"
              data-testid="share-body"
            />
          </div>

          <button
            type="submit"
            className="btn-primary w-full"
            disabled={busy || !selected || !title.trim() || (Array.isArray(groups) && groups.length === 0)}
            data-testid="share-submit"
          >
            {busy ? <><Loader2 size={13} className="animate-spin"/> Sharing…</> : <><Share2 size={13}/> Share to group</>}
          </button>
        </form>
      </div>
    </div>
  );
};

function niceKind(kind) {
  const map = {
    notes: "Notes",
    study_guide: "Study guide",
    flashcards: "Flashcard set",
    practice_test: "Practice test",
    worksheet: "Worksheet",
    study_plan: "Study plan",
  };
  return map[kind] || (kind || "Study item");
}

function defaultKindTitle(item) {
  if (!item) return "";
  const kind = niceKind(item.kind);
  // Try to pull the subject from any known field on the item
  const subj = item.subject || item.data?.subject;
  return subj ? `${kind} · ${subj}` : kind;
}

export default ShareToGroupModal;
